import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, FormsModule, ValidationErrors, Validators } from '@angular/forms'
import { EventTimingStatus, TimingExceptionHoursMode } from '@app/v2/event/enums/event.enum'
import { EventService } from '@app/v2/event/services/event.service'
import { EventTimingException, TimingExceptionPayload } from '@app/v2/event/types/event-timing-exception.type'
import { DayWeekShort } from '@app/v2/event/types/event.type'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { EMPTY, finalize } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { DateRange, DisabledYearMonthDayRange } from '../date-range-picker/date-range-picker-content/date-range-picker-content.component'
import { EVENT_TIME_PICKER_MINUTE_OPTIONS } from '../event-time-picker/event-time-picker.component'
import { TimingExceptionFormComponent } from './timing-exception-form/timing-exception-form.component'

const EXCEPTION_MINUTES = EVENT_TIME_PICKER_MINUTE_OPTIONS

function snapMinute(m: string): string {
  const value = (m || '00').padStart(2, '0').slice(0, 2)
  return (EXCEPTION_MINUTES as readonly string[]).includes(value) ? value : '00'
}

function timeToMinutes(t: string): number {
  const timeString = (t || '00:00').slice(0, 5)
  const [hour, minute] = timeString.split(':').map((value) => Number(value))
  return (hour ?? 0) * 60 + (minute ?? 0)
}

function trimmedRequired(control: AbstractControl): ValidationErrors | null {
  const v = (control.value ?? '').toString().trim()
  return v.length ? null : { required: true }
}

type ExceptionFormSnapshot = {
  title: string
  startDate: string | null
  endDate: string | null
  hoursMode: TimingExceptionHoursMode
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
}

@Component({
  selector: 'app-event-timing-exceptions',
  standalone: true,
  imports: [FormsModule, ModalComponent, NgxTippyModule, TimingExceptionFormComponent],
  templateUrl: './event-timing-exceptions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventTimingExceptionsComponent {
  readonly HoursMode = TimingExceptionHoursMode
  /** ngx-tippy options for icon-only row actions (matches other v2 surfaces). */
  readonly iconActionTippyProps = { theme: 'dark' as const }

  readonly organizationId = input<number | null>(null)
  readonly eventId = input<number | null>(null)
  /** Event public / main start date (`YYYY-MM-DD`); exception dates cannot be before this. */
  readonly eventPublicStartDate = input<string | null>(null)
  /** Event public / main end date (`YYYY-MM-DD`); exception dates cannot be after this when set. */
  readonly eventPublicEndDate = input<string | null>(null)
  /** Allowed weekdays for exceptions: dates not matching are disabled and highlighted in the picker. */
  readonly allowedWeekDays = input<DayWeekShort[] | null>(null)
  /** True while parent flow is resetting all exceptions on backend. */
  readonly isResetting = input<boolean>(false)
  /** Monotonic value incremented after reset completes; triggers ordered refetch. */
  readonly resetVersion = input<number>(0)

  readonly exceptions = signal<EventTimingException[]>([])
  readonly loading = signal(false)
  readonly savingAdd = signal(false)
  readonly savingEdit = signal(false)
  readonly savingDelete = signal(false)
  /** Shown under opening/closing time fields when “Different hours” is selected and the range is invalid. */
  readonly timeRangeError = signal<string | null>(null)
  readonly addModalId = 'event-timing-exception-add'
  readonly editModalId = 'event-timing-exception-edit'
  readonly deleteModalId = 'event-timing-exception-delete'
  readonly editHeaderIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.4516 0.621206L11.1745 1.97018L14.1146 5.07562L15.391 3.72665C15.584 3.52276 15.7372 3.2807 15.8417 3.01428C15.9462 2.74786 16 2.4623 16 2.17393C16 1.88555 15.9462 1.6 15.8417 1.33358C15.7372 1.06716 15.584 0.825093 15.391 0.621206C14.9948 0.222442 14.4685 0 13.9213 0C13.374 0 12.8478 0.222442 12.4516 0.621206Z" fill="#111928"/><path d="M12.9389 6.31744L9.99872 3.212L6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744Z" fill="#111928"/><path d="M6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744L9.99872 3.212L6.06991 7.35815Z" fill="#111928"/><path d="M9.80581 11.4894C9.57348 11.7344 9.27787 11.9015 8.95602 11.9698L6.01586 12.5907C5.74746 12.6473 5.46997 12.6331 5.20802 12.5493C4.94607 12.4655 4.70776 12.3147 4.51422 12.1103C4.32068 11.9058 4.17791 11.6541 4.09856 11.3774C4.01921 11.1008 4.00574 10.8077 4.05935 10.5242L4.64722 7.41876C4.7119 7.07881 4.87012 6.76659 5.10205 6.5212L9.193 2.20028C9.17025 2.1184 9.15849 2.03355 9.15808 1.94822H1.80185C1.3241 1.94869 0.866052 2.14935 0.528235 2.50615C0.190419 2.86296 0.000440318 3.34676 0 3.85136V14.0969C0.000440318 14.6015 0.190419 15.0853 0.528235 15.4421C0.866052 15.7989 1.3241 15.9995 1.80185 16H11.5021C11.9798 15.9995 12.4379 15.7989 12.7757 15.4421C13.1135 15.0853 13.3035 14.6015 13.3039 14.0969V7.79464L9.80581 11.4894Z" fill="#111928"/></svg>`
  readonly addHeaderIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.1111 7.11111H8.88889V0.888889C8.88889 0.653141 8.79524 0.427048 8.62854 0.260349C8.46184 0.0936505 8.23575 0 8 0C7.76425 0 7.53816 0.0936505 7.37146 0.260349C7.20476 0.427048 7.11111 0.653141 7.11111 0.888889V7.11111H0.888889C0.653141 7.11111 0.427048 7.20476 0.260349 7.37146C0.0936505 7.53816 0 7.76425 0 8C0 8.23575 0.0936505 8.46184 0.260349 8.62854C0.427048 8.79524 0.653141 8.88889 0.888889 8.88889H7.11111V15.1111C7.11111 15.3469 7.20476 15.573 7.37146 15.7397C7.53816 15.9064 7.76425 16 8 16C8.23575 16 8.46184 15.9064 8.62854 15.7397C8.79524 15.573 8.88889 15.3469 8.88889 15.1111V8.88889H15.1111C15.3469 8.88889 15.573 8.79524 15.7397 8.62854C15.9064 8.46184 16 8.23575 16 8C16 7.76425 15.9064 7.53816 15.7397 7.37146C15.573 7.20476 15.3469 7.11111 15.1111 7.11111Z" fill="#111928"/></svg>`
  readonly addTitleControl = new FormControl('', { nonNullable: true, validators: [trimmedRequired] })
  readonly editTitleControl = new FormControl('', { nonNullable: true, validators: [trimmedRequired] })
  readonly addDateDisplayControl = new FormControl('', { nonNullable: true, validators: [Validators.required] })
  readonly editDateDisplayControl = new FormControl('', { nonNullable: true, validators: [Validators.required] })

  readonly addTitleInputId: string = 'timing-exception-add-title'
  readonly addTitlePlaceholder: string = 'Exception title'
  readonly addHoursRadioName: string = 'timing-exception-add-hours'
  readonly addDateInputId: string = 'timing-exception-add-date'

  readonly editTitleInputId: string = 'timing-exception-edit-title'
  readonly editTitlePlaceholder: string = 'Title'
  readonly editHoursRadioName: string = 'timing-exception-edit-hours'
  readonly editDateInputId: string = 'timing-exception-edit-date'
  readonly formStartDate = signal<string | null>(null)
  readonly formEndDate = signal<string | null>(null)
  readonly formHoursMode = signal<TimingExceptionHoursMode>(TimingExceptionHoursMode.CLOSED)
  readonly formStartHour = signal('10')
  readonly formStartMinute = signal('00')
  readonly formEndHour = signal('22')
  readonly formEndMinute = signal('00')
  readonly editingExceptionId = signal<string | number | null>(null)
  readonly editSnapshot = signal<ExceptionFormSnapshot | null>(null)
  readonly deleteTarget = signal<EventTimingException | null>(null)

  private readonly _eventService = inject(EventService)
  private readonly _modalService = inject(ModalService)
  private readonly _alertService = inject(AlertService)
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    effect(() => {
      const allowed = this.allowedWeekDays()
      if (!allowed || allowed.length === 0) {
        untracked(() => {
          this.exceptions.set([])
          this.loading.set(false)
        })
      }
    })

    toObservable(this._ids)
      .pipe(
        switchMap((pair) => {
          if (!pair) {
            untracked(() => {
              this.exceptions.set([])
              this.loading.set(false)
            })
            return EMPTY
          }
          if (pair.resetting) {
            untracked(() => this.loading.set(true))
            return EMPTY
          }
          untracked(() => this.loading.set(true))
          return this._eventService.getTimingExceptions(pair.organizationId, pair.eventId).pipe(finalize(() => untracked(() => this.loading.set(false))))
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.exceptions.set(this._normalizeList(res.data))
        },
        error: (err: { message?: string }) => {
          this._alertService.alert(Alert.ERROR, err?.message ?? 'Failed to load timing exceptions')
        }
      })
  }

  private readonly _ids = computed(() => {
    const organizationId = this.organizationId()
    const eventId = this.eventId()
    const version = this.resetVersion()
    const resetting = this.isResetting()
    return organizationId != null && eventId != null ? ({ organizationId, eventId, version, resetting } as const) : null
  })

  /**
   * Date ranges of other **closed** exceptions only — those days are disabled/highlighted in the picker.
   * “Different hours” exceptions stay selectable on overlapping dates (current edit row excluded).
   */
  readonly dateRangesClaimedByOtherClosedExceptions = computed((): DisabledYearMonthDayRange[] => {
    const editingId = this.editingExceptionId()
    return this.exceptions()
      .filter((ex) => editingId == null || String(ex.id) !== String(editingId))
      .filter((ex) => this._exceptionStatusToHoursMode(ex.status) === TimingExceptionHoursMode.CLOSED)
      .map((ex) => ({
        start: ex.start_date <= ex.end_date ? ex.start_date : ex.end_date,
        end: ex.start_date <= ex.end_date ? ex.end_date : ex.start_date
      }))
  })

  readonly silentDisabledDateRangesAfterPublicEnd = computed((): DisabledYearMonthDayRange[] => {
    const end: string | null = this.eventPublicEndDate()
    if (!end) return []
    const d = new Date(end + 'T12:00:00')
    if (Number.isNaN(d.getTime())) return []
    d.setDate(d.getDate() + 1)
    const start = d.toISOString().slice(0, 10)
    return start <= '9999-12-31' ? [{ start, end: '9999-12-31' }] : []
  })

  /**
   * Maps API `status` to {@link TimingExceptionHoursMode} for the form and list UI.
   * Accepts `ACTIVE`, or `DIFFERENTHOURS` as “different hours”; `INACTIVE` (and anything else) as closed.
   */
  private _exceptionStatusToHoursMode(status: EventTimingException['status'] | undefined | null): TimingExceptionHoursMode {
    if (status === TimingExceptionHoursMode.DIFFERENTHOURS || status === EventTimingStatus.ACTIVE) {
      return TimingExceptionHoursMode.DIFFERENTHOURS
    }
    return TimingExceptionHoursMode.CLOSED
  }

  isOpenStatus(ex: EventTimingException): boolean {
    return this._exceptionStatusToHoursMode(ex.status) === TimingExceptionHoursMode.DIFFERENTHOURS
  }

  formatDateLine(ymd: string): string {
    const d = new Date(ymd + 'T12:00:00')
    if (isNaN(d.getTime())) return ymd
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  }

  formatRange(ex: EventTimingException): string {
    const a = this.formatDateLine(ex.start_date)
    if (ex.end_date === ex.start_date) return a
    return `${a} – ${this.formatDateLine(ex.end_date)}`
  }

  formatTimeLine(ex: EventTimingException): string {
    const sh = (ex.start_time || '00:00').slice(0, 5)
    const eh = (ex.end_time || '00:00').slice(0, 5)
    return `${sh} – ${eh}`
  }

  onFormRangeSelected(range: DateRange): void {
    this.formStartDate.set(range.startDate)
    this.formEndDate.set(range.endDate ?? range.startDate)
    this._clearDateServerErrors()
    this.addDateDisplayControl.updateValueAndValidity()
    this.editDateDisplayControl.updateValueAndValidity()
  }

  onHoursModeChange(mode: TimingExceptionHoursMode): void {
    this.formHoursMode.set(mode)
    this.timeRangeError.set(null)
  }

  onTimePartChange(): void {
    this.timeRangeError.set(null)
  }

  openAddModal(): void {
    /** Add flow must not inherit edit context; otherwise closed-day ranges stay filtered for the old row. */
    this.editingExceptionId.set(null)
    this.editSnapshot.set(null)
    this.timeRangeError.set(null)
    this.addTitleControl.reset('')
    this.addTitleControl.markAsUntouched()
    this.addDateDisplayControl.reset('')
    this.addDateDisplayControl.markAsUntouched()
    this.formStartDate.set(null)
    this.formEndDate.set(null)
    this.formHoursMode.set(TimingExceptionHoursMode.CLOSED)
    this.formStartHour.set('10')
    this.formStartMinute.set('00')
    this.formEndHour.set('22')
    this.formEndMinute.set('00')
    this._modalService.toggleModal(this.addModalId)
  }

  openEditModal(ex: EventTimingException): void {
    this.timeRangeError.set(null)
    this.editTitleControl.markAsUntouched()
    this.editDateDisplayControl.markAsUntouched()
    this.editingExceptionId.set(ex.id)
    this.editTitleControl.setValue(ex.title ?? '')
    this.formStartDate.set(ex.start_date)
    this.formEndDate.set(ex.end_date)
    const hoursMode = this._exceptionStatusToHoursMode(ex.status)
    this.formHoursMode.set(hoursMode)
    const [sh, sm] = this._parseTime(ex.start_time)
    const [eh, em] = this._parseTime(ex.end_time)
    this.formStartHour.set(sh)
    this.formStartMinute.set(sm)
    this.formEndHour.set(eh)
    this.formEndMinute.set(em)
    this._clearDateServerErrors()
    this.editSnapshot.set({
      title: ex.title ?? '',
      startDate: ex.start_date,
      endDate: ex.end_date,
      hoursMode,
      startHour: sh,
      startMinute: sm,
      endHour: eh,
      endMinute: em
    })
    this._modalService.toggleModal(this.editModalId)
  }

  openDeleteModal(ex: EventTimingException): void {
    this.deleteTarget.set(ex)
    this._modalService.toggleModal(this.deleteModalId)
  }

  closeAddModal(): void {
    this._modalService.toggleModal(this.addModalId)
  }

  closeEditModal(): void {
    this._modalService.toggleModal(this.editModalId)
    this._clearEditModalContext()
  }

  /** Backdrop / X / Escape use {@link ModalComponent.closeModal} only — no toggle here. */
  onEditModalDismissed(): void {
    this._clearEditModalContext()
  }

  private _clearEditModalContext(): void {
    this.editingExceptionId.set(null)
    this.editSnapshot.set(null)
  }

  closeDeleteModal(): void {
    this._modalService.toggleModal(this.deleteModalId)
    this.deleteTarget.set(null)
  }

  resetEditForm(): void {
    const snap = this.editSnapshot()
    if (!snap) return
    this.editTitleControl.setValue(snap.title)
    this.formStartDate.set(snap.startDate)
    this.formEndDate.set(snap.endDate)
    this.formHoursMode.set(snap.hoursMode)
    this.formStartHour.set(snap.startHour)
    this.formStartMinute.set(snap.startMinute)
    this.formEndHour.set(snap.endHour)
    this.formEndMinute.set(snap.endMinute)
    this.timeRangeError.set(null)
    this._clearDateServerErrors()
    this.editTitleControl.markAsUntouched()
    this.editDateDisplayControl.markAsUntouched()
  }

  confirmAdd(): void {
    this.addTitleControl.markAllAsTouched()
    this.addDateDisplayControl.markAllAsTouched()
    const payload = this._buildPayload(this.addTitleControl, this.addDateDisplayControl)
    if (!payload) return
    const pair = this._ids()
    if (!pair) return
    this.savingAdd.set(true)
    this._eventService
      .createTimingException(pair.organizationId, pair.eventId, payload)
      .pipe(
        finalize(() => this.savingAdd.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.closeAddModal()
          this._refetch()
        },
        error: (err: { message?: string }) => {
          this._alertService.alert(Alert.ERROR, err?.message ?? 'Failed to add exception')
        }
      })
  }

  confirmEdit(): void {
    const id = this.editingExceptionId()
    this.editTitleControl.markAllAsTouched()
    this.editDateDisplayControl.markAllAsTouched()
    const payload = this._buildPayload(this.editTitleControl, this.editDateDisplayControl)
    if (id == null || !payload) return
    const pair = this._ids()
    if (!pair) return
    this.savingEdit.set(true)
    this._eventService
      .updateTimingException(pair.organizationId, pair.eventId, id, payload)
      .pipe(
        finalize(() => this.savingEdit.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.closeEditModal()
          this._refetch()
        },
        error: (err: { message?: string }) => {
          this._alertService.alert(Alert.ERROR, err?.message ?? 'Failed to update exception')
        }
      })
  }

  confirmDelete(): void {
    const ex = this.deleteTarget()
    if (!ex) return
    const pair = this._ids()
    if (!pair) return
    this.savingDelete.set(true)
    this._eventService
      .deleteTimingException(pair.organizationId, pair.eventId, ex.id)
      .pipe(
        finalize(() => this.savingDelete.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal()
          this._refetch()
        },
        error: (err: { message?: string }) => {
          this._alertService.alert(Alert.ERROR, err?.message ?? 'Failed to remove exception')
        }
      })
  }

  public get deleteModalMessage(): string {
    const ex = this.deleteTarget()
    if (!ex) return ''
    const quoted = `\u201c${this.formatRange(ex)}\u201d`
    return `Are you sure you want to remove the exception on ${quoted}?<br>This will restore the event’s default schedule for that date.`
  }

  private _refetch(): void {
    const pair = this._ids()
    if (!pair) return
    untracked(() => this.loading.set(true))
    this._eventService
      .getTimingExceptions(pair.organizationId, pair.eventId)
      .pipe(
        finalize(() => untracked(() => this.loading.set(false))),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => this.exceptions.set(this._normalizeList(res.data)),
        error: (err: { message?: string }) => {
          this._alertService.alert(Alert.ERROR, err?.message ?? 'Failed to refresh exceptions')
        }
      })
  }

  private _normalizeList(data: unknown): EventTimingException[] {
    if (Array.isArray(data)) return data as EventTimingException[]
    return []
  }

  private _clearDateServerErrors(): void {
    for (const c of [this.addDateDisplayControl, this.editDateDisplayControl]) {
      this._stripServerErrorFromControl(c)
    }
  }

  private _stripServerErrorFromControl(c: FormControl<string>): void {
    const e = c.errors
    if (!e?.['serverError']) return
    const next = { ...e }
    delete next['serverError']
    c.setErrors(Object.keys(next).length ? next : null)
  }

  private _parseTime(t: string): [string, string] {
    const s = (t || '00:00').slice(0, 5)
    const [a, b] = s.split(':')
    const h = (a ?? '09').padStart(2, '0')
    const mRaw = (b ?? '00').padStart(2, '0')
    const m = snapMinute(mRaw)
    const hi = Math.min(23, Math.max(0, parseInt(h, 10) || 0))
    return [String(hi).padStart(2, '0'), m]
  }

  private _buildPayload(titleControl: FormControl<string>, dateDisplayControl: FormControl<string>): TimingExceptionPayload | null {
    this.timeRangeError.set(null)
    this._stripServerErrorFromControl(dateDisplayControl)
    dateDisplayControl.updateValueAndValidity({ emitEvent: false })

    const title = (titleControl.value ?? '').trim()
    if (!title || titleControl.invalid) {
      return null
    }
    let start = this.formStartDate()
    let end = this.formEndDate() ?? start
    if (!start || !end) {
      dateDisplayControl.setErrors({ required: true })
      dateDisplayControl.markAsTouched()
      return null
    }
    if (dateDisplayControl.invalid) {
      return null
    }
    if (end < start) {
      const tmp = start
      start = end
      end = tmp
      this.formStartDate.set(start)
      this.formEndDate.set(end)
    }
    const pubMin = this.eventPublicStartDate()
    if (pubMin && (start < pubMin || end < pubMin)) {
      dateDisplayControl.setErrors({ serverError: 'Exception dates must be on or after the event start date.' })
      dateDisplayControl.markAsTouched()
      return null
    }
    const mode = this.formHoursMode()
    let startTime = '00:00'
    let endTime = '23:59'
    let status: TimingExceptionHoursMode = TimingExceptionHoursMode.CLOSED
    if (mode === TimingExceptionHoursMode.DIFFERENTHOURS) {
      startTime = `${this.formStartHour()}:${this.formStartMinute()}`
      endTime = `${this.formEndHour()}:${this.formEndMinute()}`
      if (timeToMinutes(endTime) <= timeToMinutes(startTime)) {
        this.timeRangeError.set('Closing time must be after opening time.')
        return null
      }
      status = TimingExceptionHoursMode.DIFFERENTHOURS
    }
    return {
      start_date: start,
      end_date: end,
      start_time: startTime,
      end_time: endTime,
      title,
      status
    }
  }
}
